import React, { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import {
  getSeoStats,
  getRankings,
  getOpportunities,
  getCompetitors,
  detectOpportunities,
  type SeoStats,
  type RankingData,
  type Opportunity,
  type Competitor
} from '@/api/seoApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, Target, Search, BarChart3, RefreshCw } from 'lucide-react';
import { KeywordRankingsTable } from '@/components/seo/KeywordRankingsTable';
import { AddKeywordDialog } from '@/components/seo/AddKeywordDialog';
import { OpportunitiesTable } from '@/components/seo/OpportunitiesTable';
import { KeywordSuggestions } from '@/components/seo/KeywordSuggestions';
import { KeywordDiscovery } from '@/components/seo/KeywordDiscovery';

export default function SEODashboard() {
  const { selectedSite } = useSite();

  const [stats, setStats] = useState<SeoStats | null>(null);
  const [rankings, setRankings] = useState<RankingData[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [detectingOpportunities, setDetectingOpportunities] = useState(false);
  const [addKeywordOpen, setAddKeywordOpen] = useState(false);

  useEffect(() => {
    if (selectedSite) {
      loadDashboardData();
    }
  }, [selectedSite]);

  const loadDashboardData = async () => {
    if (!selectedSite) return;

    setLoading(true);
    try {
      const [statsData, rankingsData, opportunitiesData, competitorsData] = await Promise.all([
        getSeoStats(selectedSite.id || selectedSite._id),
        getRankings(selectedSite.id || selectedSite._id),
        getOpportunities(selectedSite.id || selectedSite._id),
        getCompetitors(selectedSite.id || selectedSite._id)
      ]);

      setStats(statsData);
      setRankings(rankingsData);
      setOpportunities(opportunitiesData);
      setCompetitors(competitorsData);
    } catch (error) {
      console.error('Failed to load SEO dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDetectOpportunities = async () => {
    if (!selectedSite) return;

    setDetectingOpportunities(true);
    try {
      const newOpportunities = await detectOpportunities(selectedSite.id || selectedSite._id);
      setOpportunities(newOpportunities);
    } catch (error) {
      console.error('Failed to detect opportunities:', error);
    } finally {
      setDetectingOpportunities(false);
    }
  };

  const handleKeywordAdded = () => {
    loadDashboardData();
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <div className="text-center text-muted-foreground">
          Please select a site from the header to view SEO tracking data.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center text-muted-foreground">Loading SEO dashboard...</div>
      </div>
    );
  }

  const avgPosition = stats?.average_position && typeof stats.average_position === 'number'
    ? parseFloat(stats.average_position.toFixed(1))
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">SEO Tracking</h1>
          <p className="text-muted-foreground">
            Monitor keyword rankings, SERP features, and content opportunities
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadDashboardData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <AddKeywordDialog
            siteId={selectedSite.id || selectedSite._id}
            open={addKeywordOpen}
            onOpenChange={setAddKeywordOpen}
            onKeywordAdded={handleKeywordAdded}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Keywords</CardTitle>
            <Search className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_keywords || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.ranked_keywords || 0} currently ranking
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top 3 Rankings</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.top_3_rankings || 0}</div>
            <p className="text-xs text-muted-foreground">
              Position 1-3 in search results
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top 10 Rankings</CardTitle>
            <BarChart3 className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.top_10_rankings || 0}</div>
            <p className="text-xs text-muted-foreground">
              First page visibility
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Position</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {avgPosition > 0 ? avgPosition : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              Across all ranked keywords
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="rankings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="rankings">Rankings</TabsTrigger>
          <TabsTrigger value="discovery">Discover Rankings</TabsTrigger>
          <TabsTrigger value="suggestions">Keyword Ideas</TabsTrigger>
          <TabsTrigger value="opportunities">
            Opportunities
            {opportunities.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {opportunities.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="competitors">
            Competitors
            {competitors.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {competitors.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rankings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Keyword Rankings</CardTitle>
              <CardDescription>
                Track your position in search results for targeted keywords
              </CardDescription>
            </CardHeader>
            <CardContent>
              <KeywordRankingsTable
                rankings={rankings}
                siteId={selectedSite.id || selectedSite._id}
                onRefresh={loadDashboardData}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="discovery" className="space-y-4">
          <KeywordDiscovery
            siteId={selectedSite.id || selectedSite._id}
            onKeywordAdded={loadDashboardData}
          />
        </TabsContent>

        <TabsContent value="suggestions" className="space-y-4">
          <KeywordSuggestions
            siteId={selectedSite.id || selectedSite._id}
            onKeywordAdded={loadDashboardData}
          />
        </TabsContent>

        <TabsContent value="opportunities" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Content Opportunities</CardTitle>
                <CardDescription>
                  SEO opportunities detected from SERP analysis
                </CardDescription>
              </div>
              <Button
                onClick={handleDetectOpportunities}
                disabled={detectingOpportunities}
                size="sm"
              >
                {detectingOpportunities ? 'Detecting...' : 'Detect New Opportunities'}
              </Button>
            </CardHeader>
            <CardContent>
              <OpportunitiesTable
                opportunities={opportunities}
                siteId={selectedSite.id || selectedSite._id}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="competitors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Competitor Analysis</CardTitle>
              <CardDescription>
                Domains competing for the same keywords
              </CardDescription>
            </CardHeader>
            <CardContent>
              {competitors.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  No competitors detected yet. They will appear automatically as you track keywords.
                </div>
              ) : (
                <div className="space-y-4">
                  {competitors.map((competitor) => (
                    <div
                      key={competitor.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div>
                        <div className="font-medium">{competitor.competitor_domain}</div>
                        {competitor.competitor_name && (
                          <div className="text-sm text-muted-foreground">
                            {competitor.competitor_name}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">
                          {competitor.keyword_overlap_count} shared keywords
                        </div>
                        {competitor.discovered_via && (
                          <div className="text-xs text-muted-foreground">
                            via {competitor.discovered_via}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
